"use client";

import Link from "next/link";
import Image from "next/image";
import React, { useMemo } from "react";
import { getStrapiMedia } from "@aicodepro/web-builder-utils";

/* =========================
    TYPES (Strict Data Safety)
========================= */
interface Button {
  id: string;
  url: string;
  text: string;
  type: string; // "primary" | "secondary"
  newTab: boolean;
}

interface Picture {
  data: {
    id: string;
    attributes: {
      url: string;
      alternativeText: string;
    };
  };
}

interface HeroProps {
  data: {
    id: string;
    title: string;
    description: string;
    picture: Picture;
    buttons: Button[];
    isLive: boolean;
    useH1Tag?: boolean;
  };
}

const Hero_BackgroundVisual = ({ data }: HeroProps) => {
  if (!data?.isLive) return null;

  const picUrl = getStrapiMedia(data.picture?.data?.attributes?.url);
  const picAlt =
    data?.picture?.data?.attributes?.alternativeText ||
    data?.title ||
    "Hero Background";

  // Split title logic preserved for the two-tone accent
  const { mainTitle, highlightTitle } = useMemo(() => {
    const titleWords = data?.title?.split(" ") || [];
    return {
      mainTitle: titleWords.slice(0, -2).join(" "),
      highlightTitle: titleWords.slice(-2).join(" "),
    };
  }, [data?.title]);

  const TitleTag = data?.useH1Tag ? "h1" : "h2";

  return (
    <section className="relative w-full h-[500px] md:h-[650px] lg:h-[80vh] overflow-hidden group bg-neutral-900">
      
      {/* 1. Background Visual - Subtle Slow Zoom */}
      <div className="absolute inset-0 transition-transform duration-[12000ms] ease-in-out group-hover:scale-110">
        {picUrl && (
          <Image
            src={picUrl}
            alt={picAlt}
            fill
            className="object-cover"
            priority
            quality={100}
          />
        )}
      </div>

      {/* 2. Vignette Overlay - Keeps center visual clear, darkens bottom for text */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/40 z-10" />
      <div className="absolute inset-0 bg-[radial-gradient(circle,transparent_40%,rgba(0,0,0,0.4)_100%)] z-10" />

      {/* 3. Global Clickable Layer */}
      {data?.buttons?.[0]?.url && (
        <Link
          href={data.buttons[0].url}
          className="absolute inset-0 z-20 cursor-pointer"
          aria-hidden="true"
          target={data.buttons[0].newTab ? "_blank" : "_self"}
        />
      )}

      {/* 4. Content Layer - Bottom Centered for Visual Emphasis */}
      <div className="relative h-full max-w-[1440px] mx-auto w-full px-6 flex flex-col justify-end items-center pb-16 md:pb-24 z-30 pointer-events-none">
        <div className="max-w-4xl text-center pointer-events-auto">
          
          {/* Architectural Typography - Minimalistic & Floating */}
          <div className="mb-4">
            <TitleTag className="text-4xl md:text-7xl font-black text-white uppercase tracking-[0.15em] leading-tight drop-shadow-[0_10px_10px_rgba(0,0,0,0.5)]">
              {mainTitle}{" "}
              <span style={{ color: "var(--secondary)" }}>
                {highlightTitle}
              </span>
            </TitleTag>
          </div>

          {/* Short Description */}
          {data?.description && (
            <p className="text-sm md:text-lg text-white/80 mb-8 max-w-xl mx-auto font-light tracking-[0.1em] uppercase drop-shadow-md">
              {data?.description}
            </p>
          )}

          {/* 5. Ghost Style Buttons Container */}
          <div className="flex flex-wrap justify-center gap-4">
            {data?.buttons?.map((btn, index) => {
              const isPrimary = btn.type === "primary" || (!btn.type && index === 0);
              
              return (
                <Link
                  key={btn.id || index}
                  href={btn?.url || "#"}
                  target={btn?.newTab ? "_blank" : "_self"}
                  rel={btn?.newTab ? "noopener noreferrer" : undefined}
                  className={`
                    px-10 py-3 font-bold uppercase text-[10px] tracking-[0.3em] transition-all duration-500 active:scale-95 border
                    ${
                      isPrimary
                        ? "text-white border-white bg-white/5 hover:bg-white hover:text-black"
                        : "text-white border-white/30 bg-black/20 backdrop-blur-sm hover:border-white"
                    }
                  `}
                  onMouseEnter={(e) => {
                    if (isPrimary) {
                        e.currentTarget.style.backgroundColor = "white";
                        e.currentTarget.style.color = "black";
                    } else {
                        e.currentTarget.style.borderColor = "var(--secondary)";
                        e.currentTarget.style.color = "var(--secondary)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isPrimary) {
                        e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)";
                        e.currentTarget.style.color = "white";
                    } else {
                        e.currentTarget.style.borderColor = "";
                        e.currentTarget.style.color = "";
                    }
                  }}
                >
                  {btn?.text}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero_BackgroundVisual;